import { useState } from 'react';
import { useLoaderData, useNavigate, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { 
  Briefcase, Calendar, Building2, MapPin, Clock, 
  TrendingUp, Search, Filter, Eye, 
  Trash2, ExternalLink, Plus
} from 'lucide-react';
import { requireUser } from '../../../lib/session.server';
import { applicationsApi } from '../../../lib/api';
import { Card, Button, Badge } from '../../../components/ui';
import { ApplicationStatusChip, useToastActions } from '../../../components/ui';
import type { ApplicationsResponse, User } from '../../../types';
import type { ApplicationStatus } from '../../../types/applications';
import { APPLICATION_STATUS_CONFIG } from '../../../types/applications';

interface ApplicationsData {
  user: User;
  applicationsResponse: ApplicationsResponse;
  statusFilter?: ApplicationStatus;
  searchQuery: string;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const url = new URL(request.url);
  
  const page = parseInt(url.searchParams.get('page') || '1', 10);
  const status = url.searchParams.get('status') as ApplicationStatus | undefined;
  const search = url.searchParams.get('search') || '';
  
  try {
    const applicationsResponse = await applicationsApi.getApplications({
      page,
      pageSize: 20,
      sort: 'applied_date',
      order: 'desc',
      filters: {
        ...(status && { status: [status] }),
        ...(search && { search }),
      },
    });

    return {
      user,
      applicationsResponse,
      statusFilter: status,
      searchQuery: search,
    };
  } catch (error) {
    const emptyResponse: ApplicationsResponse = {
      applications: [],
      total_count: 0,
      page: 1,
      page_size: 20,
      has_more: false,
      status_counts: {} as any,
      bucket_counts: {} as any,
    };
    
    return {
      user,
      applicationsResponse: emptyResponse,
      statusFilter: status,
      searchQuery: search,
      error: 'Failed to load applications',
    };
  }
}

export function ApplicationsPage() {
  const { applicationsResponse, statusFilter, searchQuery } = useLoaderData() as ApplicationsData;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const { success, error } = useToastActions();

  const { applications, total_count, page, page_size, has_more, status_counts } = applicationsResponse;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString();
  };

  const handleStatusFilter = (status: ApplicationStatus | '') => {
    const params = new URLSearchParams(searchParams);
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams);
    if (localSearchQuery.trim()) {
      params.set('search', localSearchQuery.trim());
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleQuickAction = async (applicationId: string, action: 'view' | 'edit' | 'delete') => {
    switch (action) {
      case 'view':
        navigate(`/applications/${applicationId}`);
        break;
      case 'edit':
        navigate(`/applications/${applicationId}`);
        break;
      case 'delete':
        if (confirm('Are you sure you want to delete this application?')) {
          try {
            await applicationsApi.deleteApplication(applicationId);
            success('Application Deleted', 'The application has been removed.');
            window.location.reload();
          } catch (err) {
            error('Delete Failed', 'Unable to delete application.');
          }
        }
        break;
    }
  };

  const getDaysAgo = (dateString: string): number => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Briefcase className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Applications</h1>
        </div>
        <p className="text-muted-foreground">
          Track and manage your job applications with AI-powered insights.
        </p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        {/* Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search applications..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleSearch} variant="secondary" size="sm" className="gap-1">
            <Search className="h-4 w-4" />
            Search
          </Button>
          <Button 
            onClick={() => setShowFilters(!showFilters)} 
            variant="secondary" 
            size="sm" 
            className="gap-1 lg:hidden"
          >
            <Filter className="h-4 w-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Status Filters */}
      <Card>
        <div className="flex flex-wrap gap-2 p-1">
          <button
            onClick={() => handleStatusFilter('')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex-1 sm:flex-initial ${
              !statusFilter
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            All ({total_count})
          </button>
          {(['applied', 'interviewing', 'offered', 'rejected', 'withdrawn'] as ApplicationStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex-1 sm:flex-initial ${
                statusFilter === status
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {APPLICATION_STATUS_CONFIG[status].label} ({status_counts[status] || 0})
            </button>
          ))}
        </div>
      </Card>

      {/* Applications Grid */}
      {applications.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Briefcase className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No applications found</h3>
            <p className="text-muted-foreground mb-4">
              {statusFilter 
                ? `No applications with status "${statusFilter}"`
                : searchQuery
                ? `No applications match "${searchQuery}"`
                : "Start tracking your job applications here."}
            </p>
            <Button onClick={() => navigate('/jobs/search')} className="gap-2">
              <Plus className="h-4 w-4" />
              Find Jobs to Apply
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4">
            {applications.map((application) => {
              const daysAgo = getDaysAgo(application.applied_at);
              const needsFollowUp = daysAgo > 14 && application.status === 'applied';
              
              return (
                <Card 
                  key={application.id} 
                  className={`transition-all hover:shadow-lg cursor-pointer ${
                    needsFollowUp ? 'ring-2 ring-amber-500/20 border-amber-500/30' : ''
                  }`}
                  onClick={() => navigate(`/applications/${application.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Job Info */}
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary/10 rounded-xl">
                          <Briefcase className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground text-lg">
                            {application.job_title}
                          </h3>
                          <div className="flex items-center gap-4 mt-1 text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-4 w-4" />
                              {application.company}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {application.location}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Applied {formatDate(application.applied_at)}
                        </span>
                        {application.updated_at !== application.applied_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            Updated {formatDate(application.updated_at)}
                          </span>
                        )}
                        {needsFollowUp && (
                          <Badge variant="secondary" className="gap-1 text-amber-600 bg-amber-500/10">
                            <TrendingUp className="h-3 w-3" />
                            Follow up needed
                          </Badge>
                        )}
                      </div>

                      {/* Notes Preview */}
                      {application.notes && (
                        <p className="text-muted-foreground mt-2 text-sm line-clamp-2">
                          {application.notes}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-start gap-2 ml-4">
                      <ApplicationStatusChip status={application.status} />
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickAction(application.id, 'view');
                          }}
                          className="gap-1 opacity-60 hover:opacity-100"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {application.job_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(application.job_url, '_blank');
                            }}
                            className="gap-1 opacity-60 hover:opacity-100"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickAction(application.id, 'delete');
                          }}
                          className="gap-1 opacity-60 hover:opacity-100 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {has_more && (
            <div className="flex justify-center gap-2">
              {page > 1 && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', (page - 1).toString());
                    setSearchParams(params);
                  }}
                >
                  Previous
                </Button>
              )}
              
              <div className="flex items-center px-4 py-2 text-sm text-muted-foreground">
                Page {page} • Showing {(page - 1) * page_size + 1}-{Math.min(page * page_size, total_count)} of {total_count} applications
              </div>
              
              <Button
                variant="secondary"
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page + 1).toString());
                  setSearchParams(params);
                }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}